/**
 * BuddyGroupPanel — Buddy group selection for the registration flow.
 *
 * Shown during the registration wizard when the rodeo has buddy_groups_enabled.
 * Allows users to:
 *   1. See their current buddy group (if any)
 *   2. Create a new buddy group
 *   3. Join an existing buddy group by entering a group code / name
 *
 * The selected buddy_group_id is passed up to the registration form
 * so it's included in the registration payload.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Users, Plus, UserPlus, CheckCircle, Loader2, AlertCircle, Ban } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useCreateBuddyGroup,
  useJoinBuddyGroup,
  useSetPrimaryCardholder,
} from '@/api/extensions/useBuddyGroupOps';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getMyGroup } from '@/services/buddy-group.service';
import { useBuddyGroups } from '@/hooks/useBuddyGroups';
import { SavedBuddyGroupPicker } from './SavedBuddyGroupPicker';
import { InlineBuddyGroupCreate } from './InlineBuddyGroupCreate';
import { BuddyGroupCardholderSelect } from './BuddyGroupCardholderSelect';
import type { BuddyGroupDetail } from '@/types/buddy-groups';

interface BuddyGroupPanelProps {
  rodeoId: string;
  /** Called when the user selects/creates/joins a buddy group */
  onGroupSelected: (groupId: string) => void;
  /** Called when the user opts out of a buddy group */
  onGroupCleared: () => void;
  /** Currently selected buddy_group_id (from form state) */
  selectedGroupId: string;
  /** Current buddy_group_position value (from form state) */
  selectedPosition?: number | null;
  /** Called when position changes */
  onPositionChanged?: (position: number | null) => void;
  /** Max buddy group size for this rodeo (for position range) */
  maxGroupSize?: number;
  /** When true, non-members are blocked from buddy groups */
  isNonMemberBlocked?: boolean;
  /** When true, multi-entry events disable buddy groups */
  multiEntryDisabled?: boolean;
  /** Card owner org ID from the registration form's card identity field */
  cardOwnerOrgId?: string;
  /** Card owner user ID from the registration form's card identity field */
  cardOwnerUserId?: string;
  /**
   * When true, the user is already in an active/locked buddy group for
   * this rodeo and cannot opt out. Hides the "Register without group"
   * affordance so the hard rule is honored visually.
   */
  buddyGroupRequired?: boolean;
}

export function BuddyGroupPanel({
  rodeoId,
  onGroupSelected,
  onGroupCleared,
  selectedGroupId,
  selectedPosition,
  onPositionChanged,
  maxGroupSize = 4,
  isNonMemberBlocked,
  multiEntryDisabled,
  cardOwnerOrgId,
  cardOwnerUserId,
  buddyGroupRequired,
}: BuddyGroupPanelProps) {
  const { data: myGroup, isLoading: groupLoading } = useQuery<BuddyGroupDetail | null>({
    queryKey: ['buddy-group-ops', 'my-group', rodeoId],
    queryFn: () => getMyGroup(rodeoId),
    enabled: !!rodeoId,
  });
  const { data: savedGroups } = useBuddyGroups();
  const queryClient = useQueryClient();
  const createGroup = useCreateBuddyGroup();
  const joinGroup = useJoinBuddyGroup();
  const setPrimary = useSetPrimaryCardholder();

  const [mode, setMode] = useState<'view' | 'create' | 'join'>('view');
  const [joinGroupId, setJoinGroupId] = useState('');
  const [joinFirstName, setJoinFirstName] = useState('');
  const [joinLastName, setJoinLastName] = useState('');
  const [joinDob, setJoinDob] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Non-member blocked state
  if (isNonMemberBlocked) {
    return (
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
        <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
          <Ban className="h-4 w-4" />
          <span className="text-sm">Buddy groups are not available for non-members.</span>
        </div>
      </div>
    );
  }

  // Multi-entry disabled state with tooltip
  if (multiEntryDisabled) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="rounded-lg border border-muted bg-muted/30 p-3 opacity-60 cursor-not-allowed">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Users className="h-4 w-4" />
                <span className="text-sm">Buddy groups unavailable</span>
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            Buddy groups are disabled for events that allow multiple entries
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // If user already has a group, show it
  if (groupLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-4">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Checking buddy group...</span>
      </div>
    );
  }

  // User already in a buddy group
  if (myGroup) {
    const isSelected = selectedGroupId === myGroup.id;
    return (
      <div className="space-y-3">
        <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-4 space-y-2">
          <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
            <Users className="h-4 w-4" />
            <span className="text-sm font-medium">
              Your Buddy Group: {myGroup.group_name}
            </span>
          </div>
          <div className="text-sm space-y-1">
            {myGroup.members?.map((m) => (
              <div key={m.id} className="flex items-center gap-2">
                <span>
                  {m.display_name ?? 'Unknown'}
                  {m.member_number && (
                    <span className="text-muted-foreground ml-1">
                      #{m.member_number}
                    </span>
                  )}
                </span>
                {m.is_primary && (
                  <span className="text-xs bg-green-500/20 text-green-600 dark:text-green-400 px-1.5 py-0.5 rounded">
                    Primary
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <Button
            size="sm"
            variant={isSelected ? 'default' : 'outline'}
            onClick={() => onGroupSelected(myGroup.id)}
          >
            <CheckCircle className="h-3.5 w-3.5 mr-1" />
            {isSelected ? 'Selected' : 'Use this group'}
          </Button>
          {isSelected && onPositionChanged && (
            <div className="flex items-center gap-1.5">
              <Label className="text-xs whitespace-nowrap">Position:</Label>
              <select
                value={selectedPosition ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  onPositionChanged(val ? parseInt(val, 10) : null);
                }}
                className="h-8 rounded-md border border-input bg-background px-2 py-1 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">Auto</option>
                {Array.from({ length: maxGroupSize }, (_, i) => i + 1).map(
                  (n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  )
                )}
              </select>
            </div>
          )}
          {isSelected && !buddyGroupRequired && (
            <Button size="sm" variant="ghost" onClick={onGroupCleared}>
              Register without group
            </Button>
          )}
        </div>
        {isSelected && myGroup.members && myGroup.members.length > 1 && myGroup.primary_member_user_profile_id && (
          <BuddyGroupCardholderSelect
            members={myGroup.members}
            currentPrimaryId={myGroup.primary_member_user_profile_id}
            onChangePrimary={async (userProfileId) => {
              try {
                await setPrimary.mutateAsync({
                  groupId: myGroup.id,
                  user_profile_id: userProfileId,
                });
                // Refresh the group data so the UI reflects the new primary
                queryClient.invalidateQueries({ queryKey: ['buddy-group-ops', 'my-group', rodeoId] });
              } catch {
                // Error is handled by React Query's mutation error state
              }
            }}
            isLoading={setPrimary.isPending}
          />
        )}
      </div>
    );
  }

  // No buddy group — show create/join options
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Buddy groups keep you and your friends together in the draw order.
        You can create a new group or join an existing one.
      </p>

      {/* Saved groups — filtered to same rodeo only.
          Cross-rodeo groups are excluded because
          validateRegistrationBuddyGroup() rejects them. With M:N
          attachments a group can be attached to many rodeos, so we
          check `attached_rodeo_ids` (authoritative) before falling
          back to the legacy single `rodeo_id` column. */}
      {(() => {
        const sameRodeoGroups = (savedGroups ?? []).filter((g) =>
          g.attached_rodeo_ids && g.attached_rodeo_ids.length > 0
            ? g.attached_rodeo_ids.includes(rodeoId)
            : g.rodeo_id === rodeoId,
        );
        if (sameRodeoGroups.length === 0 || mode !== 'view') return null;
        return (
          <SavedBuddyGroupPicker
            savedGroups={sameRodeoGroups}
            onSelect={(groupId) => onGroupSelected(groupId)}
            selectedId={selectedGroupId}
          />
        );
      })()}

      {mode === 'view' && (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setMode('create');
              setError(null);
            }}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Create Group
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setMode('join');
              setError(null);
            }}
          >
            <UserPlus className="h-3.5 w-3.5 mr-1" />
            Join Group
          </Button>
          {selectedGroupId && (
            <Button size="sm" variant="ghost" onClick={onGroupCleared}>
              Skip
            </Button>
          )}
        </div>
      )}

      {mode === 'create' && (
        <div className="space-y-2">
          <InlineBuddyGroupCreate
            rodeoId={rodeoId}
            onCreated={(groupId) => {
              onGroupSelected(groupId);
              setMode('view');
            }}
            createMutation={createGroup}
            cardOwnerOrgId={cardOwnerOrgId}
            cardOwnerUserId={cardOwnerUserId}
            existingNames={(savedGroups ?? []).map((g) => g.group_name)}
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setMode('view')}
          >
            Cancel
          </Button>
        </div>
      )}

      {mode === 'join' && (
        <div className="space-y-3 rounded-lg border p-3">
          <div>
            <Label>Buddy Group ID</Label>
            <Input
              placeholder="Paste the group ID from your friend"
              value={joinGroupId}
              onChange={(e) => setJoinGroupId(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>First Name</Label>
              <Input
                value={joinFirstName}
                onChange={(e) => setJoinFirstName(e.target.value)}
              />
            </div>
            <div>
              <Label>Last Name</Label>
              <Input
                value={joinLastName}
                onChange={(e) => setJoinLastName(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label>Date of Birth</Label>
            <Input
              type="date"
              value={joinDob}
              onChange={(e) => setJoinDob(e.target.value)}
            />
          </div>
          {error && (
            <div className="flex items-start gap-2 text-xs text-destructive">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              {error}
            </div>
          )}
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={
                joinGroup.isPending ||
                !joinGroupId ||
                !joinFirstName ||
                !joinLastName ||
                !joinDob
              }
              onClick={async () => {
                setError(null);
                try {
                  await joinGroup.mutateAsync({
                    buddy_group_id: joinGroupId,
                    first_name: joinFirstName,
                    last_name: joinLastName,
                    date_of_birth: joinDob,
                  });
                  // Backend returns { member: {...}, group_size } not flat BuddyGroup.
                  // Use the joinGroupId we already have as the selected group.
                  onGroupSelected(joinGroupId);
                  setMode('view');
                } catch (err: any) {
                  setError(err?.message ?? 'Failed to join group');
                }
              }}
            >
              {joinGroup.isPending ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : (
                <UserPlus className="h-3.5 w-3.5 mr-1" />
              )}
              Join
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setMode('view')}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
